import React from 'react';

/**
 * Minimal error boundary used to contain render-time exceptions in
 * non-critical UI (e.g. the Settings panel). Without containment, a single
 * render throw blanks the entire React tree — exactly the symptom Eric
 * reported when clicking the Settings gear.
 *
 * Usage:
 *   <ErrorBoundary fallback={<div>Settings unavailable — close and retry.</div>}>
 *     <SettingsPanel ... />
 *   </ErrorBoundary>
 *
 * Keep this component tiny and dependency-free so it cannot itself throw.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    try { console.error('[ErrorBoundary]', error, info?.componentStack); } catch (_) {}
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      const { fallback, label } = this.props;
      if (fallback) return typeof fallback === 'function' ? fallback(this.state.error, this.reset) : fallback;
      return (
        <div
          role="alert"
          data-testid="error-boundary-fallback"
          style={{
            padding: '16px',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.08)',
            color: 'var(--text-primary, #fff)',
            fontSize: '13px',
            lineHeight: 1.5
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>
            {label ? `${label} unavailable` : 'Something went wrong'}
          </div>
          <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '8px' }}>
            This panel hit an error and was contained so the rest of the app
            keeps working. Close this panel and try again.
          </div>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
